import "./styles.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Container, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import {
  CFMModuleName,
  CustomerFeedback,
  CustomerFeedbackAlignment,
  CustomerFeedbackIconPosition,
  CustomerFeedbackPopUpAlignment,
} from "common/components/CustomerFeedback";
import FAQWidget from "common/components/FAQWidget/FAQWidget";
import HardwareSupportWidget from "common/components/HardwareSupportWidget/HardwareSupportWidget";
import { Hero } from "common/components/Hero";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import RecommendedItemsFactory from "common/factories/recommended-items-factory";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { TrafficFrom } from "common/helpers/traffic-from";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { useRecommendations } from "hooks/useRecommendations";
import { UserPermissions } from "lib/services";
import React, { FC } from "react";
import { useHistory } from "react-router-dom";

import aiBanner from "../../../../public/img/ai-banner.svg";

const pageSectionPadding = {
  paddingTop: 32,
  paddingBottom: 32,
};

const HomePage: FC = () => {
  const useHardwareShop = UserPermissions.current.hasPermission(PERMISSIONS.USE_HARDWARE_SHOP);
  const useSmartRecs = UserPermissions.current.hasPermission(PERMISSIONS.USE_SMART_RECS);

  const toggleSearchExperience = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_SEARCH_EXPERIENCE);
  const history = useHistory();

  const TOTAL_TOP_PICKS_ITEMS = 4;
  const TOP_PICKS_ITEMS_PER_ROW = useHardwareShop ? 2 : 4;
  const TOTAL_COMMUNICATION_TOOLS_ITEMS = useHardwareShop ? 3 : 4;
  const COMMUNICATION_TOOLS_ITEMS_PER_ROW = useHardwareShop ? 3 : 4;
  const TOTAL_POPULAR_CHOICE_ITEMS = 4;
  const POPULAR_CHOICE_ITEMS_PER_ROW = useHardwareShop ? 2 : 4;

  // Use the hook to get all recommendation data in one place
  const {
    sectionTitle: smartRecommendationsSectionTitle,
    itemsData: recommendedItemsData,
    isLoading: isLoadingRecommendations,
    error: recommendationsError,
  } = useRecommendations({
    totalItems: TOTAL_POPULAR_CHOICE_ITEMS,
    enabled: useSmartRecs,
  });

  const onClick = (event: any) => {
    trackUxmCustomEvent("ai-banner-button", {});
    history.push(Paths.aiforme.url);
  };

  const sectionTitle = (text: string, id: string) => {
    return (
      <Text data-cy={id} fontSize={"T300"} fontWeight="bold" color="neutral90">
        {text}
      </Text>
    );
  };

  return (
    <Col>
      {/* Search box container*/}
      <PageMainContainer className="search-banner">
        <Hero toggleSearchExperience={toggleSearchExperience} />
      </PageMainContainer>

      {/* Content container */}
      <PageMainContainer>
        {/* AI Applications banner */}
        <Container data-cy="ai-banner" dataTestId="ai-banner" {...pageSectionPadding}>
          <Col className="ai-banner" borderRadius={16}>
            <Row>
              <img className="ai-banner__image" src={aiBanner} alt="My Icon" />
            </Row>
            <Row justifyContent="space-between" padding={[16, 32]}>
              <Col rowGap={8}>
                {sectionTitle(getString("home.aiBanner.header"), "top-picks-title")}
                <Row>
                  <Text>{getString("home.aiBanner.text")}</Text>
                </Row>
              </Col>
              <Col justifyContent="flex-end">
                <Button
                  data-cy="ai-banner-button"
                  dataTestId="ai-banner-button"
                  variant={ButtonVariant.Primary}
                  onClick={onClick}
                >
                  {getString("home.aiBanner.button")}
                </Button>
              </Col>
            </Row>
          </Col>
        </Container>
        {/* Most popular & Hardware row*/}

        {/* Team Recommendations */}
        {/* Shown only when SmartRecs are active */}
        {useSmartRecs && (
          <Container
            {...pageSectionPadding}
            dataTestId="popular-in-team-section"
            data-test-section="smart-recommendations"
          >
            <Row gridGap={35} display="flex" alignItems="flex-start">
              <Col gridGap={32} flex={1}>
                <Col gridGap={"S300"}>
                  {sectionTitle(getString(smartRecommendationsSectionTitle), "popularChoice-tools-title")}
                  <RecommendedItemsFactory
                    totalItems={TOTAL_POPULAR_CHOICE_ITEMS}
                    perRow={POPULAR_CHOICE_ITEMS_PER_ROW}
                    categoryToFilterBy="none"
                    useSmartRecommendations={true}
                    recommendedItemsData={recommendedItemsData}
                    isLoadingRecommendations={isLoadingRecommendations}
                    toggleSearchExperience={toggleSearchExperience}
                    recommendationsError={recommendationsError}
                    trafficFrom={TrafficFrom.PopularChoices}
                  >
                    <CustomerFeedback
                      moduleName={CFMModuleName.popularInTeam}
                      alignment={CustomerFeedbackAlignment.Left}
                      position={CustomerFeedbackIconPosition.Left}
                      popUpXAlignment={CustomerFeedbackPopUpAlignment.Right}
                    />
                  </RecommendedItemsFactory>
                </Col>
              </Col>

              {useHardwareShop && <HardwareSupportWidget />}
            </Row>
          </Container>
        )}

        {/* Top Picks */}
        <Container {...pageSectionPadding} data-test-section="top-picks">
          <Row gridGap={35} display="flex" alignItems="flex-start">
            <Col gridGap={32} flex={1}>
              <Col gridGap={"S300"}>
                {sectionTitle(getString("home.foundationalSectionHeader"), "top-picks-title")}
                <RecommendedItemsFactory
                  totalItems={TOTAL_TOP_PICKS_ITEMS}
                  perRow={TOP_PICKS_ITEMS_PER_ROW}
                  categoryToFilterBy="topPicks"
                  trafficFrom={TrafficFrom.TopPicks}
                >
                  <CustomerFeedback
                    moduleName={CFMModuleName.mostPopular}
                    alignment={CustomerFeedbackAlignment.Left}
                    position={CustomerFeedbackIconPosition.Left}
                    popUpXAlignment={CustomerFeedbackPopUpAlignment.Right}
                  />
                </RecommendedItemsFactory>
              </Col>
            </Col>

            {/* Hardware widget stays alongside Top-picks */}
            {useHardwareShop && !useSmartRecs && <HardwareSupportWidget />}
          </Row>
        </Container>

        {/* Communication tools */}
        {/* Shown only when SmartRecs are NOT active */}
        {!useSmartRecs && (
          <Container {...pageSectionPadding} data-test-section="communication-tools">
            <Col gridGap={"S300"}>
              {sectionTitle(getString("home.communicationTools"), "communication-tools-title")}
              <RecommendedItemsFactory
                totalItems={TOTAL_COMMUNICATION_TOOLS_ITEMS}
                perRow={COMMUNICATION_TOOLS_ITEMS_PER_ROW}
                categoryToFilterBy="communicationTools"
                toggleSearchExperience={toggleSearchExperience}
                trafficFrom={TrafficFrom.CommunicationTools}
                useSmartRecommendations={false}
              >
                <CustomerFeedback
                  moduleName={CFMModuleName.amazonEssentials}
                  alignment={CustomerFeedbackAlignment.Left}
                  position={CustomerFeedbackIconPosition.Left}
                  popUpXAlignment={CustomerFeedbackPopUpAlignment.Right}
                />
              </RecommendedItemsFactory>
            </Col>
          </Container>
        )}

        {/* Most common questions */}
        <Container {...pageSectionPadding}>
          <FAQWidget />
        </Container>
      </PageMainContainer>
    </Col>
  );
};

export default HomePage;
